import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colors, typography } from '../../styles/theme';
import { useMarketTone } from '../marketTone';

/** Piezas compartidas por la tarjeta y el informe (paleta C, barra, aviso único). */

export const MARKET_FONT = typography.fontFamily.primary;
/** Caracteres de la leyenda (izq. + der.) que entran a 11 px en la tarjeta de 360 px. */
export const MARKET_LEGEND_FIT_CHARS = 54;
export const MARKET_TEAL_LIGHT = '#7CC5C4';
export const marketSkeletonSx = { bgcolor: 'rgba(255,255,255,0.08)' };
export const marketSmallSx = { fontFamily: MARKET_FONT, fontSize: '12px', color: colors.textSecondary, lineHeight: 1.35 };
export const marketTextSx = { fontFamily: MARKET_FONT, fontSize: '13px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.35 };
export const marketLinkButtonSx = {
  textTransform: 'none',
  fontFamily: MARKET_FONT,
  fontSize: '13px',
  fontWeight: 600,
  color: colors.primaryLight,
  minWidth: 0,
  px: 0.75,
  py: 0,
  height: 28,
  '&:hover': { bgcolor: 'rgba(72,146,145,0.12)' },
};
export const marketOutlineButtonSx = {
  ...marketLinkButtonSx,
  px: 1.25,
  border: '1px solid rgba(72,146,145,0.6)',
  borderRadius: '8px',
};

/** Paleta C (teal Terrio + flechas): «En línea» relleno; Alto / Bajo con borde, flecha y %. */
const chipBaseSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.25,
  px: 1.25,
  borderRadius: '999px',
  fontFamily: MARKET_FONT,
  fontWeight: 600,
  lineHeight: 1,
  flexShrink: 0,
  whiteSpace: 'nowrap',
};
const CHIP_SIZE_SX = {
  sm: { height: 20, fontSize: '11px', px: 1 },
  md: { height: 24, fontSize: '12px' },
  lg: { height: 28, fontSize: '13px' },
};

export function VerdictChip({ verdict, label, size = 'md', testId = 'market-verdict-chip' }) {
  const tone = useMarketTone();
  const colorSx = tone.chip[verdict] || tone.chip.neutral;
  const iconSize = size === 'sm' ? 18 : 20;
  return (
    <Box
      component="span"
      sx={{ ...chipBaseSx, ...CHIP_SIZE_SX[size], ...colorSx }}
      data-testid={testId}
      data-verdict={verdict || 'none'}
    >
      {verdict === 'alto' ? <ArrowDropUpIcon sx={{ fontSize: iconSize, my: -0.5 }} aria-hidden /> : null}
      {verdict === 'bajo' ? <ArrowDropDownIcon sx={{ fontSize: iconSize, my: -0.5 }} aria-hidden /> : null}
      {verdict === 'en_linea' ? (
        <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FFFFFF', mr: 0.5 }} />
      ) : null}
      {label}
    </Box>
  );
}

/** Barra: rango medio (P25–P75), mediana y la unidad. Posiciones de `computeMarketBar`. */
export function MarketBar({ bar, verdict }) {
  const t = useMarketTone();
  if (!bar) return <Box sx={{ height: 8, my: 0.75 }} />;
  return (
    <Box
      data-testid="market-bar"
      sx={{ position: 'relative', height: 8, borderRadius: '4px', bgcolor: t.barBg, my: 0.75, flexShrink: 0 }}
    >
      {bar.iqrLeft !== null && bar.iqrWidth !== null ? (
        <Box sx={{
          position: 'absolute', top: 0, bottom: 0, left: `${bar.iqrLeft}%`, width: `${bar.iqrWidth}%`,
          bgcolor: t.barIqr, borderRadius: '4px',
        }}
        />
      ) : null}
      {bar.median !== null ? (
        <Box sx={{ position: 'absolute', top: -3, left: `${bar.median}%`, ml: '-1px', width: 2, height: 14, bgcolor: t.barMedian }} />
      ) : null}
      {bar.value !== null ? (
        <Box
          data-testid="market-bar-unit"
          data-left={bar.value}
          sx={{
            position: 'absolute', top: -5, left: `${bar.value}%`, ml: '-6px', width: 12, height: 18,
            borderRadius: '3px', border: `2px solid ${t.barUnitBorder}`, boxSizing: 'border-box',
            bgcolor: verdict === 'en_linea' ? t.barUnitOnLine : colors.primaryLight,
          }}
        />
      ) : null}
    </Box>
  );
}

/** Debajo de la barra: «Rango medio USD 1.417–1.984/m²» · «Tu unidad USD 2.000/m²». */
export function MarketBarLegend({
  rangeLabel, unitLabel, rangePrefix = 'Rango medio', rangeTitle = undefined, fit = false,
}) {
  const left = rangeLabel ? `${rangePrefix} ${rangeLabel}` : '';
  const right = unitLabel ? `Tu unidad ${unitLabel}` : '';
  // `fit` (tarjeta angosta, alquiler en total por mes): sin «…»; con textos largos baja a 10 px
  // para entrar en una línea a 360 px («Esperable ARS 1.100.000–1.400.000 · Tu unidad ARS 1.250.000/mes»).
  const t = useMarketTone();
  const fontSize = fit && (left.length + right.length) > MARKET_LEGEND_FIT_CHARS ? '10px' : '11px';
  const textSx = fit ? { whiteSpace: 'nowrap', flexShrink: 0 } : {};
  const small = { ...marketSmallSx, color: t.muted };
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, minWidth: 0 }}
      data-testid="market-bar-legend"
      data-fit={fit ? fontSize : undefined}
    >
      <Typography component="span" sx={{ ...small, fontSize, ...textSx }} noWrap={!fit} title={rangeTitle}>
        {left}
      </Typography>
      <Typography component="span" sx={{ ...small, fontSize, color: t.legendRight, ...textSx }} noWrap={!fit}>
        {right}
      </Typography>
    </Box>
  );
}

/** Aviso único del informe (dudosos, sin datos, falta de dato, otra operación, error). */
export function MarketNotice({ title, text, actionLabel, onAction, testId = 'market-notice' }) {
  const t = useMarketTone();
  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.5, borderRadius: '10px',
        bgcolor: t.noticeBg, border: `1px solid ${t.noticeBorder}`, flexShrink: 0,
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 20, color: t.noticeIcon, mt: '1px' }} aria-hidden />
      <Box sx={{ minWidth: 0 }}>
        {title ? <Typography sx={{ ...marketTextSx, fontWeight: 600, color: t.noticeTitle }}>{title}</Typography> : null}
        {text ? <Typography sx={{ ...marketTextSx, color: t.noticeText, mt: title ? 0.5 : 0 }}>{text}</Typography> : null}
        {actionLabel && onAction ? (
          <Button size="small" onClick={onAction} sx={{ ...marketOutlineButtonSx, mt: 1.25 }}>{actionLabel}</Button>
        ) : null}
      </Box>
    </Box>
  );
}
